import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { routePaths } from 'router';
import { useDeleteCargoItems } from 'api/cargo-items/use-delete-cargo-items.ts';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import { CargoItemEdit } from 'components/cargos/cargo-item-edit';
import { CargoItemStatusHistory } from 'components/cargos/cargo-item-status-history';
import type { ICargoItemsControllerProps } from './types';

export const CargoItemsControllerComponent: React.FC<ICargoItemsControllerProps> = (props) => {
  const { cargoItem, cargoType, isLastItem } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allowStatusHistory = useRoleGuard([UserRole.admin]);

  const { mutateAsync: deleteCargoItem } = useDeleteCargoItems();

  const [modal, contextHolder] = Modal.useModal();
  const [isActiveControl, setIsActiveControl] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isStatusHistoryOpen, setIsStatisHistoryOpen] = useState(false);

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const openEditDrawer = () => setIsEditOpen(true);
  const closeEditDrawer = () => {
    setIsEditOpen(false);
    deactivateWithTimeout();
  };

  const openStatusHistoryDrawer = () => setIsStatisHistoryOpen(true);
  const closeStatusHistoryDrawer = () => {
    setIsStatisHistoryOpen(false);
    deactivateWithTimeout();
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: isLastItem ? t('cargo:delete_last_item_confirm_text') : t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteCargoItem({ cargoId: cargoItem.cargoId, cargoItemsIds: [cargoItem.id] });
        deactivateWithTimeout();
        if (isLastItem) {
          navigate(routePaths.cargos);
        }
      },
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      confirm();
    } else if (key === 'edit') {
      openEditDrawer();
    } else if (key === 'status_history') {
      openStatusHistoryDrawer();
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'edit',
              label: t('common:edit'),
              icon: <EditOutlined />,
            },
            ...(allowStatusHistory
              ? [
                  {
                    key: 'status_history',
                    label: t('common:status_history'),
                    icon: <HistoryOutlined />,
                  },
                ]
              : []),
            {
              key: 'delete',
              label: t('common:delete'),
              icon: <DeleteOutlined />,
            },
          ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
      {isActiveControl ? (
        <CargoItemEdit cargoItem={cargoItem} cargoType={cargoType} isOpen={isEditOpen} onClose={closeEditDrawer} />
      ) : null}
      {isActiveControl ? (
        <CargoItemStatusHistory
          cargoId={cargoItem.cargoId}
          cargoItemId={cargoItem.id}
          isOpen={isStatusHistoryOpen}
          onClose={closeStatusHistoryDrawer}
        />
      ) : null}
    </>
  );
};

export const CargoItemsController = withRoleGuard(CargoItemsControllerComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
]);
